import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { faExpand } from '@fortawesome/free-solid-svg-icons';
import { AttributeComponent } from '@hub4b/controls/attribute-form-item/attribute.component';
import { NgxSpinnerModule } from 'ngx-spinner';
import { takeUntil } from 'rxjs';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { BaseDirective } from 'src/app/core/directives/base.directive';
import { AvmAttributeFieldPipe } from 'src/app/core/pipes/avm-attribute-field.pipe';
import { BoxGroupInfos } from 'src/app/models/box-group-infos';
import { PropertyType } from 'src/app/models/enums/property-type';
import { AttributeDefinition } from 'src/app/models/server/attribute-definition';
import { ViewType } from 'src/app/models/server/enums/view-type';
import { Folder } from 'src/app/models/server/folder';
import { View } from 'src/app/models/server/view';
import { SpinnerService } from 'src/app/services/core/spinner.service';
import { AttributesSectionEventsService } from 'src/app/services/events/attributes-section-events.service';
import { BoxesStateService } from 'src/app/services/state/boxes-state.service';
import { DetailBaseDatasource } from '../../../datasources/detail-base-datasource';

@Component({
    selector: 'app-attributes-section',
    templateUrl: './attributes-section.component.html',
    styleUrls: ['./attributes-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatFormFieldModule, MatSelectModule, NgClass, MatOptionModule, AttributeComponent, NgxSpinnerModule, AvmAttributeFieldPipe]
})
export class AttributesSectionComponent extends BaseDirective implements OnInit {
    /**
     * Références des icônes utilisés dans le template.
     */
    public faExpand = faExpand;

    /**
     * Références des Enums utilisées dans le template.
     */
    public ViewType = ViewType;
    public PropertyType = PropertyType;

    /**
     * Références des constantes utilisées dans le template.
     */
    public WORKFLOW_ID = FolderPropertyCodes.VIEW_ID;
    public BOX_ID = FolderPropertyCodes.BOX_ID;
    public CREATION_DATE = FolderPropertyCodes.CREATION_DATE;
    public UPDATE_DATE = FolderPropertyCodes.UPDATE_DATE;
    public ID = FolderPropertyCodes.ID;

    public filteredAttributesDefinitions: AttributeDefinition[];

    /**
     * Liste des définitions des attributs.
     */
    @Input()
    public set attributesDefinitions(value: AttributeDefinition[]) {
        this.filteredAttributesDefinitions = value.filter((x) => x.invisible === false);
    }

    /**
     * Dossier pour lequel les attributs sont affichés.
     */
    private _folder: Folder = null;

    /**
     * Spécifie le dossier pour lequel les attributs sont affichés.
     */
    @Input()
    public set folder(value: Folder) {
        this._folder = value;
    }

    /**
     * Obtient le dossier pour lequel les attributs sont affichés.
     */
    public get folder(): Folder {
        return this._folder;
    }

    /**
     * Id de la vue à laquelle appartient le dossier.
     */
    @Input()
    public view: View;

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Liste des groupes de bannettes avec les bannettes qu'ils contiennent. Utilisé pour l'attribut 'Bannette'
     */
    public boxGroupInfo: BoxGroupInfos[];

    /**
     * Constructeur
     * @param folderActionsService
     * @param boxesStateService
     * @param spinner
     * @param attributesSectionEventsService
     * @param cdr
     */
    constructor(
        private boxesStateService: BoxesStateService,
        private spinner: SpinnerService,
        private attributesSectionEventsService: AttributesSectionEventsService,
        private cdr: ChangeDetectorRef,
        private datasource: DetailBaseDatasource
    ) {
        super();
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        // TODO : ne charger que si la liste des définitions contient une définition pour l'attribut bannette.
        this.boxGroupInfo = this.boxesStateService.getBoxesGroupsList();
        this.subscribeToEvents();
    }

    /**
     * Appelée lorsque l'utilisateur modifie un attribut.
     * @param attributeCode : Code de l'attribut modifié.
     * @param newValue : Nouvelle valeur de l'attribut.
     */
    public onUpdateAttribute(attributeCode: string, newValue: any): void {
        var attribute = this.filteredAttributesDefinitions.find((x) => x.propertyCode === attributeCode);

        if (attribute.custom === true) {
            this.updateCustomAttribute(attributeCode, attribute.id, newValue);
        } else if (attribute.custom === false) {
            this.updateSystemAttribute(attributeCode, attribute.id, newValue);
        }
    }

    /**
     * Appelée lorsque l'utilisateur modifie la bannette dans laquelle se trouve le dossier.
     * @param boxId : Id de la nouvelle bannette.
     */
    public onUpdateBoxAttribute(boxId: string): void {
        this.datasource.moveFolderToBox(this.view.id, this.folder, boxId);
    }

    /**
     * * S'abonne aux événements des autres composants.
     */
    private subscribeToEvents() {
        // Souscrit aux evenements necessitant de raffraichir la section 'Attributs' du dossier.
        this.attributesSectionEventsService.needRefresh.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            if (!!event && event.folder.id == this._folder.id) {
                //console.warn(`Réception de l'événement AttributesSectionRefreshEvent par le composant AttributesComponent du dossier ${this._folder.id}`, event.folder);
                this.spinner.show('attributes-spinner');
                this._folder = event.folder;
                this.cdr.markForCheck();
                this.spinner.hide('attributes-spinner');
            }
        });
    }

    /**
     * Met à jour un attribut système.
     * @param attributeCode : Code de l'attribut modifié.
     * @param attributeId : Id de l'attribut modifié.
     * @param newValue : Nouvelle valeur de l'attribut.
     */
    private updateSystemAttribute(attributeCode: string, attributeId: number, newValue: any) {
        const oldValue = this.folder[attributeCode];

        if (oldValue != newValue) {
            this.folder[attributeCode] = newValue;
            this.datasource.updateFolderAttribute(this.view.id, this.folder, attributeId, oldValue, newValue);
        }
    }

    /**
     * Met à jour un attribut personnalisé.
     * @param attributeCode : Code de l'attribut modifié.
     * @param attributeId : Id de l'attribut modifié.
     * @param newValue : Nouvelle valeur de l'attribut.
     */
    private updateCustomAttribute(attributeCode: string, attributeId: number, value: any) {
        const oldValue = this.folder.customAttributes[attributeCode];

        if (oldValue != value) {
            this.folder.customAttributes[attributeCode] = value;
            this.datasource.updateFolderAttribute(this.view.id, this.folder, attributeId, oldValue, value);
        }
    }
}
